/* eslint-disable max-lines-per-function */
import React from "react";
import moment from "moment";
import { Box, Button, Card, CardActionArea, CardActions, Chip, Grid, Stack, Typography } from "@mui/material";
import { Email, Phone } from "@mui/icons-material";
import { InterestStatus } from "model/interest/status";
import {
  PropertiesPage_propertiesPage as PropertyInfo,
  PropertiesPage_propertiesPage_viewerSubmissions as Submission,
} from "types/gql-types";
import RemoveUserModal from "pages/dashboard/clients/interests/list/remove-user-modal";
import RemoveDraftModal from "pages/dashboard/clients/interests/list/remove-draft-modal";
import { toKabobCase } from "pages/dashboard/interests/utils";

const formatDate = (submittedAt: number | null) =>
  (submittedAt ? moment(new Date(submittedAt * 1000)).format("M/D/YY") : "");

const activeSubmissions = (app: Submission) =>
  (app.submittedAt !== null || app.submissionId !== null) &&
  app.currentStatus !== InterestStatus.Removed &&
  app.currentStatus !== InterestStatus.MovedIn;

const getActiveAppLink = (app: Submission) => (
  app.submissionId ? `/application/${app.submissionId}` : (
    `/dash/client/submissions/${app.interestListId}/${app.interestId}`));

const PropertyCardTop: React.FC<{ property: PropertyInfo }> = ({ property }) => (
  <Box
    sx={{
      background: "linear-gradient(0deg, rgba(59,65,79,1) 0%, rgba(37,74,132,1) 100%)",
      borderTopLeftRadius: "6px",
      borderTopRightRadius: "6px",
      p: 2.5,
      color: "white",
    }}
  >
    <Box sx={{ my: 1.25 }}>
      <Typography component="h2" sx={{ color: "white", fontSize: 16, fontWeight: 700 }}>
        {property.name}
      </Typography>
      <Typography variant="body2">{property.orgName}</Typography>
    </Box>
    {Boolean(property.unitSizes.length) && (
      <Box>
        {property.unitSizes.map((unitSize) => (
          <Chip
            key={`unit-${toKabobCase(unitSize)}`}
            label={unitSize}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ m: 0.25 }}
          />
        ))}
      </Box>
    )}
    <Typography variant="body2">{property.address1}</Typography>
    <Typography variant="body2">
      {property.city}, {property.state} {property.zip}
    </Typography>
  </Box>
);

const PropertyCardAction: React.FC<{
  property: PropertyInfo,
  onApplicationRemoved: () => void
}> = ({ property, onApplicationRemoved }) => {
  const [isModalOpen, setModalOpen] = React.useState(false);
  const [isDraftModalOpen, setDraftModalOpen] = React.useState(false);

  const movedIn = property.viewerSubmissions
    .map(x => x.currentStatus)
    .includes(InterestStatus.MovedIn);

  const previouslyRemoved = property.viewerSubmissions
    .map(x => x.currentStatus)
    .includes(InterestStatus.Removed);

  const activeApps = property.viewerSubmissions.filter(activeSubmissions);
  const now = new Date();
  const nowSec = Date.parse(now.toDateString()) / 1000;
  const activeApp =
    activeApps.length ? activeApps
      .sort((a, b) => (a.submittedAt || nowSec) - (b.submittedAt || nowSec))[0] : null;

  const handleOnRequestClose = (isRemoved: boolean) => {
    setModalOpen(false);
    setDraftModalOpen(false);
    if (isRemoved) onApplicationRemoved();
  };

  return (
    <Grid container direction="column" sx={{ width: "100%", textAlign: "center" }}>
      {movedIn && (
        <Grid>
          <p className="text-center">You&apos;re moved-in to this property</p>
        </Grid>
      )}

      {!movedIn && activeApp && (
        <Grid>
          <p className="text-center">
            {activeApp.submittedAt ? `Applied ${formatDate(activeApp.submittedAt)}` : "Draft"}
          </p>
          <Button
            color="secondary"
            variant="outlined"
            href={getActiveAppLink(activeApp)}
            size="large"
            fullWidth
          >
            {activeApp.submittedAt ? "Update Application" : "Continue Application"}
          </Button>
          <p className="text-center" style={{ paddingTop: 10 }}>
            {activeApp.submissionId ? (<a
              onClick={() => setDraftModalOpen(true)}
              role="button">Remove draft</a>) : (<a
              onClick={() => setModalOpen(true)}
              role="button">Remove me</a>)}

          </p>
          {activeApp.interestId && (
            <RemoveUserModal
              isOpen={isModalOpen}
              onRequestClose={handleOnRequestClose}
              interestListName={property.name || ""}
              interestId={activeApp.interestId}
            />)}
          {activeApp.submissionId && (
            <RemoveDraftModal
              isOpen={isDraftModalOpen}
              onRequestClose={handleOnRequestClose}
              submissionId={activeApp.submissionId}
              interestListName={property.name || ""}
              formType="Application"
            />
          )}
        </Grid>
      )}

      {!movedIn && !activeApp && (
        <Button
          href={`${property.preAppUrl}?utm_source=HCprop`}
          color="primary"
          variant="contained"
          size="large"
          fullWidth
        >{ previouslyRemoved ? "Reapply Now" : "Apply Now"}</Button>
      )}
    </Grid>
  );
};

const Property: React.FC<{
  property: PropertyInfo,
  onApplicationRemoved: () => void
}> = ({ property, onApplicationRemoved }) => (
  <Grid key={property.id} size={{ xs: 12, sm: 12, md: 4, lg: 3 }}>
    <Card>
      <CardActionArea>
        <PropertyCardTop property={property} />
        <Box sx={{ p: 2.5 }}>
          <Stack direction="row" spacing={2.5} alignItems="center" sx={{ mb: 1.5 }}>
            <Phone fontSize="small" />
            <a href={`tel:${property.phone}`}>{property.phone}</a>
          </Stack>
          <Stack direction="row" spacing={2.5} alignItems="center">
            <Email fontSize="small" />
            <a href={`mailto:${property.email}`}>{property.email}</a>
          </Stack>
        </Box>
      </CardActionArea>
      <CardActions sx={{ display: "flex", flexDirection: "column", justifyContent: "center", padding: 1, width: "100%" }}>
        <PropertyCardAction property={property} onApplicationRemoved={onApplicationRemoved} />
      </CardActions>
    </Card>
  </Grid>
);

export default Property;
